import { AxiosResponse } from 'axios'
import { api } from 'API'

export interface ICoachDashboardScope {
  /** Omitted = every organization the coach is staff in. */
  organizationId?: number
  /** Omitted = every Team in the scope above. */
  teamId?: number
}

export interface ICoachDashboardStats {
  /** Null when one Team is selected — counting it says nothing. */
  activeTeams: number | null
  upcoming: number
  /** 0–100, or null when the scope has no assigned work to measure. */
  completion: number | null
}

export interface ICoachScheduleEvent {
  id: number
  name: string
  scheduledDate: string
  startTime: string
  /** Derived server-side from the plan's periods — events store no end time. */
  endTime: string
  /** Minutes. */
  duration: number
  location: string
  organizationId: number
  /** Teams the event belongs to; the calendar card is labelled with the first. */
  teams: Array<{ id: number; name: string }>
}

/**
 * The coach dashboard's numbers.
 *
 * `/dashboard/*` is org-agnostic on the server: it resolves which organizations
 * the caller may read, so "all organizations" is this one call rather than one per
 * organization. Scope travels in `organizationId` rather than the interceptor's
 * `orgId`, which is skipped for this path — the two are spelt differently on
 * purpose so a stamped app-wide context cannot be mistaken for the one the
 * dashboard is asking about.
 */
export async function getCoachDashboardStats(scope: ICoachDashboardScope = {}): Promise<ICoachDashboardStats> {
  const response: AxiosResponse = await api.get('/dashboard/coach-stats', {
    params: { organizationId: scope.organizationId, teamId: scope.teamId }
  })
  return response.data
}

/** Practice events for the calendar, in the same three scopes. */
export async function getCoachSchedule(
  from: string,
  to: string,
  scope: ICoachDashboardScope = {}
): Promise<ICoachScheduleEvent[]> {
  const response: AxiosResponse = await api.get('/dashboard/coach-schedule', {
    params: { from, to, organizationId: scope.organizationId, teamId: scope.teamId }
  })
  return response.data
}
